import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { FormBuilderMethodType, PopupDynamicFieldView } from 'constants/formBuilder';
import { getDynamicFieldCatalogName, getNextViewId } from 'utils/forms';

import { openConfirmationDialogAction } from 'store/global/actions';

import { formsTestIds } from '../../forms.testIds';

import {
  ACTIONS_STACK_SPACING,
  ChangeFieldIcon,
  DeleteFieldIcon,
  FormatIcon,
  HEADER_STACK_SPACING,
  LABELS_BY_VIEW,
} from './DynamicFieldEditDeleteOptionsView.constants';
import {
  eyebrowStyles,
  getFooterBoxStyles,
  headerBoxStyles,
  rowStyles,
  stackRootStyles,
  titleStyles,
} from './DynamicFieldEditDeleteOptionsView.styles';

const DynamicFieldEditDeleteOptionsView = ({
  type,
  value,
  editor,
  setParentView,
  ignoreUserDefinedStyles = false,
  dataDynamicLabel = '',
  dataDynamicTitle = '',
  displayName = '',
  ...props
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const formatViewId = getNextViewId({ type, ignoreUserDefinedStyles });
  const hasFormatView = formatViewId !== PopupDynamicFieldView.FIELDS_LIST_VIEW;
  const formatLabel = LABELS_BY_VIEW[formatViewId] || 'Edit formatting';

  const catalogName = getDynamicFieldCatalogName({
    dynamicFieldsNamesById: editor.config?.get('dynamicFieldConfig')?.dynamicFieldsNamesById,
    value,
  });

  const displayLabel =
    displayName || catalogName || dataDynamicTitle || dataDynamicLabel || 'Untitled field';

  const footerStyles = useMemo(() => getFooterBoxStyles(theme), [theme]);

  const onDynamicFieldItemDelete = useCallback(
    e => {
      e.stopPropagation();

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm deleting',
        description: 'Are you sure you want to delete this item?',
        onAccept: () => {
          editor.execute(FormBuilderMethodType.DELETE_REACT_COMPONENT, {
            ...props,
            dataDynamicLabel,
            dataDynamicTitle,
          });
          setParentView(null);
        },
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [dispatch, editor, setParentView, props, dataDynamicLabel, dataDynamicTitle],
  );

  const onChangeField = useCallback(() => {
    setParentView(PopupDynamicFieldView.FIELDS_LIST_VIEW);
  }, [setParentView]);

  const onEditFormatting = useCallback(() => {
    setParentView(formatViewId);
  }, [setParentView, formatViewId]);

  return (
    <Stack spacing={HEADER_STACK_SPACING} sx={stackRootStyles}>
      <Box sx={headerBoxStyles}>
        <Typography color="text.secondary" sx={eyebrowStyles} variant="bold12">
          Dynamic field
        </Typography>
        <Typography color="text.primary" sx={titleStyles} variant="bold14">
          {displayLabel}
        </Typography>
      </Box>

      <Stack spacing={ACTIONS_STACK_SPACING}>
        <Button
          fullWidth
          color="primary"
          data-testid={formsTestIds.DYNAMIC_FIELD_EDIT_DELETE_CHANGE_FIELD_BUTTON}
          startIcon={<ChangeFieldIcon size={IconSize[20]} />}
          sx={rowStyles}
          variant="contained"
          onClick={onChangeField}
        >
          Change field
        </Button>

        {hasFormatView && (
          <Button
            fullWidth
            color="primary"
            data-testid={formsTestIds.DYNAMIC_FIELD_EDIT_DELETE_EDIT_FORMATTING_BUTTON}
            startIcon={<FormatIcon size={IconSize[20]} />}
            sx={rowStyles}
            variant="text"
            onClick={onEditFormatting}
          >
            {formatLabel}
          </Button>
        )}
      </Stack>

      <Box sx={footerStyles}>
        <IconButton
          aria-label="Delete"
          color="error"
          data-testid={formsTestIds.DYNAMIC_FIELD_EDIT_DELETE_DELETE_BUTTON}
          size="small"
          title="Delete"
          onClick={onDynamicFieldItemDelete}
        >
          <DeleteFieldIcon size={IconSize[20]} />
        </IconButton>
      </Box>
    </Stack>
  );
};

DynamicFieldEditDeleteOptionsView.propTypes = {
  editor: PropTypes.object.isRequired,
  setParentView: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  ignoreUserDefinedStyles: PropTypes.bool,
  dataDynamicLabel: PropTypes.string,
  dataDynamicTitle: PropTypes.string,
  displayName: PropTypes.string,
};

export default DynamicFieldEditDeleteOptionsView;
